import './time-lapse.scss';
import {useState, useEffect, useContext} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {TimeLapseValue} from '../../../interfaces/time-lapse-value';
import {TimeLapseType} from '../../../enums/time-lapse-type';
import {translations} from '../../../i18n/translations';
import {SelectInput} from '../../select-input/select-input';
import {getTimeLapseRange} from '../../../utils/functions/get-time-lapse-range';
import {AppContext} from '../../../contexts/app/context';
import {AppContextType} from '../../../contexts/app/types';
import {ThemeMode} from '../../../enums/theme-mode';
import DateRangeIcon from '@mui/icons-material/DateRange';
import moment from 'moment';
import {DateRangeCalendar} from '../../date-range-calendar/date-range-calendar';

/**
 * Time lapse controller used in the area, line, bar, and stack charts.
 */
export const TimeLapse = (props: {
  values: TimeLapseValue;
  handleChange: (values: TimeLapseValue) => void;
  disabled?: boolean;
  className?: string;
}) => {
  // Get context
  const appContext: AppContextType = useContext(AppContext);

  // Hold state
  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [values, setValues] = useState<TimeLapseValue>(props.values);

  // Translations
  const {t} = useTranslation();

  // Update values when the props update
  useEffect(() => {
    if (
      props.values.type !== values.type ||
      props.values.range[0] !== values.range[0] ||
      props.values.range[1] !== values.range[1]
    ) {
      setValues(props.values);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.values]);

  // Handle modal
  const handleOpenModal = () => {
    setModalOpened(true);
  };

  // Handle change type
  const handleChangeType = (type: string) => {
    // Update the type and range
    setValues({
      type: type as TimeLapseType,
      range:
        type === TimeLapseType.Custom
          ? values.range
          : getTimeLapseRange(type as Exclude<TimeLapseType, TimeLapseType.Custom>),
    });
  };

  // Handle cancel
  const handleCancel = () => {
    // Reset to initial values
    setValues(props.values);

    // Close the modal
    setModalOpened(false);
  };

  // Handle confirm
  const handleConfirm = () => {
    if (
      values.range[0] &&
      values.range[1] &&
      moment(values.range[0]).isValid() &&
      moment(values.range[1]).isValid()
    ) {
      // Change values
      props.handleChange(values);
    } else {
      // Reset to initial values
      setValues(props.values);
    }

    // Close the modal
    setModalOpened(false);
  };

  return (
    <>
      <Box className={props.className}>
        <Button
          disabled={props.disabled === true ? true : false}
          onClick={handleOpenModal}
          variant="outlined"
          size="large"
          className="time-lapse-button"
          startIcon={<DateRangeIcon />}
        >
          {props.values.type === TimeLapseType.Custom
            ? `${moment(props.values.range[0]).format('l')} - ${moment(props.values.range[1]).format('l')}`
            : t(translations.time_lapse_types[props.values.type])}
        </Button>
      </Box>
      <Dialog scroll="paper" onClose={handleCancel} open={modalOpened}>
        <DialogTitle className="time-lapse-dialog-title">
          {t(translations.time_lapse.dialog_title)}
        </DialogTitle>
        <DialogContent className="time-lapse-dialog-content">
          <SelectInput
            className="time-lapse-range-type-select"
            label={t(translations.time_lapse.range_label)}
            value={values.type}
            items={[
              {
                label: t(translations.time_lapse_types[TimeLapseType.ThisWeek]),
                value: TimeLapseType.ThisWeek,
              },
              {
                label: t(translations.time_lapse_types[TimeLapseType.LastWeek]),
                value: TimeLapseType.LastWeek,
              },
              {
                label: t(translations.time_lapse_types[TimeLapseType.ThisMonth]),
                value: TimeLapseType.ThisMonth,
              },
              {
                label: t(translations.time_lapse_types[TimeLapseType.LastMonth]),
                value: TimeLapseType.LastMonth,
              },
              {
                label: t(translations.time_lapse_types[TimeLapseType.Last30Days]),
                value: TimeLapseType.Last30Days,
              },
              {
                label: t(translations.time_lapse_types[TimeLapseType.Last180Days]),
                value: TimeLapseType.Last180Days,
              },
              {
                label: t(translations.time_lapse_types[TimeLapseType.Last365Days]),
                value: TimeLapseType.Last365Days,
              },
              // TODO: Missing implementation
              // {
              //   label: t(translations.time_lapse_types[TimeLapseType.Custom]),
              //   value: TimeLapseType.Custom,
              // },
            ]}
            handleChange={handleChangeType}
          />
          <Box className="time-lapse-range-type-list">
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  selected={values.type === TimeLapseType.ThisWeek}
                  onClick={() => handleChangeType(TimeLapseType.ThisWeek)}
                >
                  <ListItemText
                    primary={t(translations.time_lapse_types[TimeLapseType.ThisWeek])}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  selected={values.type === TimeLapseType.LastWeek}
                  onClick={() => handleChangeType(TimeLapseType.LastWeek)}
                >
                  <ListItemText
                    primary={t(translations.time_lapse_types[TimeLapseType.LastWeek])}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  selected={values.type === TimeLapseType.ThisMonth}
                  onClick={() => handleChangeType(TimeLapseType.ThisMonth)}
                >
                  <ListItemText
                    primary={t(translations.time_lapse_types[TimeLapseType.ThisMonth])}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  selected={values.type === TimeLapseType.LastMonth}
                  onClick={() => handleChangeType(TimeLapseType.LastMonth)}
                >
                  <ListItemText
                    primary={t(translations.time_lapse_types[TimeLapseType.LastMonth])}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  selected={values.type === TimeLapseType.Last30Days}
                  onClick={() => handleChangeType(TimeLapseType.Last30Days)}
                >
                  <ListItemText
                    primary={t(translations.time_lapse_types[TimeLapseType.Last30Days])}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  selected={values.type === TimeLapseType.Last180Days}
                  onClick={() => handleChangeType(TimeLapseType.Last180Days)}
                >
                  <ListItemText
                    primary={t(translations.time_lapse_types[TimeLapseType.Last180Days])}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  selected={values.type === TimeLapseType.Last365Days}
                  onClick={() => handleChangeType(TimeLapseType.Last365Days)}
                >
                  <ListItemText
                    primary={t(translations.time_lapse_types[TimeLapseType.Last365Days])}
                  />
                </ListItemButton>
              </ListItem>
              {/* TODO: Missing implementation */}
              {/* <ListItem disablePadding>
                <ListItemButton
                  selected={values.type === TimeLapseType.Custom}
                  onClick={() => handleChangeType(TimeLapseType.Custom)}
                >
                  <ListItemText primary={t(translations.time_lapse_types[TimeLapseType.Custom])} />
                </ListItemButton>
              </ListItem> */}
            </List>
          </Box>
          <Box className="time-lapse-date-range-and-calendar">
            <Box className="time-lapse-date-range">
              <Typography
                variant="body1"
                component="p"
                color={
                  values.range[0] && moment(values.range[0]).isValid()
                    ? undefined
                    : 'text.secondary'
                }
                sx={{
                  borderBottom:
                    values.range[0] && moment(values.range[0]).isValid() ? '1px solid' : undefined,
                  borderColor:
                    values.range[0] && moment(values.range[0]).isValid()
                      ? 'primary.main'
                      : undefined,
                }}
              >
                {`${
                  values.range[0] && moment(values.range[0]).isValid()
                    ? moment(values.range[0]).format('l')
                    : t(translations.time_lapse.start_text)
                }`}
              </Typography>
              <Typography variant="body1" component="p" className="time-lapse-to-text">
                {t(translations.time_lapse.to_text)}
              </Typography>
              <Typography
                variant="body1"
                component="p"
                color={
                  values.range[1] && moment(values.range[1]).isValid()
                    ? undefined
                    : 'text.secondary'
                }
                sx={{
                  borderBottom:
                    values.range[1] && moment(values.range[1]).isValid() ? '1px solid' : undefined,
                  borderColor:
                    values.range[1] && moment(values.range[1]).isValid()
                      ? 'primary.main'
                      : undefined,
                }}
              >
                {`${
                  values.range[1] && moment(values.range[1]).isValid()
                    ? moment(values.range[1]).format('l')
                    : t(translations.time_lapse.end_text)
                }`}
              </Typography>
            </Box>
            <DateRangeCalendar
              start={moment(values.range[0])}
              end={moment(values.range[1])}
              disableFuture
              className={`time-lapse-date-calendar-${
                appContext.state.currentThemeMode === ThemeMode.Dark ? 'dark' : 'light'
              }`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="inherit">
            {t(translations.time_lapse.cancel_button)}
          </Button>
          <Button onClick={handleConfirm} color="primary">
            {t(translations.time_lapse.confirm_button)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
